import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  addDays,
  addMonths,
  format,
  startOfDay,
} from 'date-fns';
import { useAuth } from '../../contexts/AuthContext';
import { api, supabase } from '../../lib/api';
import { Button, Select } from '../../components/ui/Kit';
import Spinner from '../../components/ui/Spinner';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { useToast } from '../../components/ui/Toast';
import CalendarMonthView from '../../components/Calendar/CalendarMonthView';
import CalendarTimeGridView from '../../components/Calendar/CalendarTimeGridView';
import DayDetailPanel from '../../components/Calendar/DayDetailPanel';
import AppointmentDetailModal from '../../components/Calendar/AppointmentDetailModal';
import SelectLeadModal from '../../components/Calendar/SelectLeadModal';
import BookAppointmentModal from '../../components/Appointments/BookAppointmentModal';
import {
  APPOINTMENT_STATUSES,
  APPOINTMENT_TYPES_FILTER,
  deleteGoogleEventsForAppointment,
  filterAppointments,
  getViewRange,
  mergeAppointment,
} from '../../lib/calendarUtils';
import { getActiveDirectoryUsers, useUsersDirectory } from '../../lib/usersDirectory';

const { FiChevronLeft, FiChevronRight } = FiIcons;

const VIEW_OPTIONS = [
  { value: 'month', label: 'Month' },
  { value: 'week', label: 'Week' },
  { value: 'day', label: 'Day' },
];

export default function Calendar() {
  const { user } = useAuth();
  const { addToast } = useToast();
  const { users: directoryUsers } = useUsersDirectory();

  const [viewMode, setViewMode] = useState('month');
  const [focusDate, setFocusDate] = useState(() => startOfDay(new Date()));
  const [selectedDate, setSelectedDate] = useState(() => startOfDay(new Date()));
  const [appointments, setAppointments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterAgent, setFilterAgent] = useState('');
  const [filterType, setFilterType] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [mobileSheetOpen, setMobileSheetOpen] = useState(false);

  const agents = useMemo(
    () => getActiveDirectoryUsers(directoryUsers).filter((u) => u.role !== 'super_admin'),
    [directoryUsers],
  );

  const [detailAppt, setDetailAppt] = useState(null);
  const [bookLead, setBookLead] = useState(null);
  const [bookExisting, setBookExisting] = useState(null);
  const [bookInitialDate, setBookInitialDate] = useState(null);
  const [bookInitialSlot, setBookInitialSlot] = useState(null);
  const [bookOpen, setBookOpen] = useState(false);
  const [selectLeadOpen, setSelectLeadOpen] = useState(false);
  const [pendingBook, setPendingBook] = useState(null);

  useEffect(() => {
    document.title = 'Calendar — Prosperity Leads';
  }, []);

  const filteredAppointments = useMemo(
    () =>
      filterAppointments(appointments, {
        agentId: filterAgent,
        type: filterType,
        status: filterStatus,
      }),
    [appointments, filterAgent, filterType, filterStatus],
  );

  const loadAppointments = useCallback(async () => {
    if (!user?.agency_id) return;
    setLoading(true);
    try {
      const { startDate, endDate } = getViewRange(viewMode, focusDate);
      const rows = await api.appointments.getUpcoming({
        agencyId: user.agency_id,
        startDate,
        endDate,
      });
      setAppointments(rows || []);
    } catch (e) {
      addToast({ type: 'error', title: 'Could not load appointments', message: e.message });
    } finally {
      setLoading(false);
    }
  }, [user?.agency_id, viewMode, focusDate, addToast]);

  useEffect(() => {
    loadAppointments();
  }, [loadAppointments]);

  useEffect(() => {
    if (!user?.agency_id || !supabase) return undefined;

    let debounceId = null;
    const scheduleReload = () => {
      if (debounceId != null) window.clearTimeout(debounceId);
      debounceId = window.setTimeout(() => {
        debounceId = null;
        loadAppointments();
      }, 1000);
    };

    const channel = supabase
      .channel('calendar-appointments')
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'appointments',
          filter: `agency_id=eq.${user.agency_id}`,
        },
        scheduleReload,
      )
      .on(
        'postgres_changes',
        { event: 'UPDATE', schema: 'public', table: 'appointments' },
        (payload) => {
          if (payload.new?.agency_id === user.agency_id) {
            setAppointments((prev) => mergeAppointment(prev, payload.new));
          }
        },
      )
      .subscribe();

    return () => {
      if (debounceId != null) window.clearTimeout(debounceId);
      supabase.removeChannel(channel);
    };
  }, [user?.agency_id, loadAppointments]);

  const agentOptions = useMemo(
    () => [
      { value: '', label: 'All Agents' },
      ...agents.map((a) => ({ value: a.id, label: a.full_name || 'Agent' })),
    ],
    [agents],
  );

  const navigateMonth = (delta) => {
    setFocusDate((d) => addMonths(d, delta));
  };

  const navigateWeekDay = (delta) => {
    setFocusDate((d) => addDays(d, delta));
    if (viewMode === 'day') setSelectedDate((d) => addDays(d, delta));
  };

  const goToday = () => {
    const today = startOfDay(new Date());
    setFocusDate(today);
    setSelectedDate(today);
  };

  const handleSelectDay = (day) => {
    setSelectedDate(day);
    setMobileSheetOpen(true);
  };

  const openBookForAppointment = async (appt) => {
    let lead = appt.lead;
    if ((!lead || !lead.agency_id) && appt.lead_id) {
      try {
        lead = await api.leads.getById(appt.lead_id);
      } catch (e) {
        addToast({ type: 'error', title: 'Could not load lead', message: e.message });
        return;
      }
    }
    if (!lead?.id) {
      addToast({ type: 'error', title: 'Lead required to reschedule' });
      return;
    }
    openBookFlow({ lead, existing: appt });
  };

  const openBookFlow = ({ date, slotStart, lead, existing }) => {
    const ymd = format(date || selectedDate, 'yyyy-MM-dd');
    setBookInitialDate(ymd);
    setBookInitialSlot(slotStart || null);
    setBookExisting(existing || null);
    if (lead) {
      setBookLead(lead);
      setBookOpen(true);
    } else {
      setPendingBook({ date: date || selectedDate, slotStart });
      setSelectLeadOpen(true);
    }
  };

  const handleLeadSelected = (lead) => {
    setBookLead(lead);
    if (pendingBook?.slotStart) setBookInitialSlot(pendingBook.slotStart);
    if (pendingBook?.date) setBookInitialDate(format(pendingBook.date, 'yyyy-MM-dd'));
    setPendingBook(null);
    setBookOpen(true);
  };

  const handleComplete = async (apptId) => {
    try {
      await api.appointments.updateStatus(apptId, 'completed');
      addToast({ type: 'success', title: 'Appointment marked complete' });
      await loadAppointments();
    } catch (e) {
      addToast({ type: 'error', title: 'Update failed', message: e.message });
    }
  };

  const handleCancel = async (apptId) => {
    const appt = appointments.find((a) => a.id === apptId);
    try {
      if (appt) await deleteGoogleEventsForAppointment(appt);
      await api.appointments.updateStatus(apptId, 'cancelled');
      addToast({ type: 'success', title: 'Appointment cancelled' });
      await loadAppointments();
    } catch (e) {
      addToast({ type: 'error', title: 'Cancel failed', message: e.message });
    }
  };

  const headerLabel =
    viewMode === 'month'
      ? format(focusDate, 'MMMM yyyy')
      : viewMode === 'week'
        ? `Week of ${format(focusDate, 'MMM d, yyyy')}`
        : format(focusDate, 'MMMM d, yyyy');

  const onPrev = () => (viewMode === 'month' ? navigateMonth(-1) : navigateWeekDay(-1));
  const onNext = () => (viewMode === 'month' ? navigateMonth(1) : navigateWeekDay(1));

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Calendar</h1>
        <p className="text-sm text-gray-500 mt-1">View and manage agency appointments.</p>
      </div>

      <div className="flex flex-wrap gap-3 items-center">
        <div className="w-40">
          <Select
            value={filterAgent}
            onChange={(e) => setFilterAgent(e.target.value)}
            options={agentOptions}
          />
        </div>
        <div className="w-40">
          <Select
            value={filterType}
            onChange={(e) => setFilterType(e.target.value)}
            options={APPOINTMENT_TYPES_FILTER}
          />
        </div>
        <div className="w-40">
          <Select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            options={APPOINTMENT_STATUSES}
          />
        </div>
      </div>

      <div className="flex flex-col lg:flex-row gap-6">
        <div className="flex-1 min-w-0 space-y-4">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="flex items-center gap-2">
              <button
                type="button"
                className="p-2 rounded-md border border-gray-200 hover:bg-gray-50 text-gray-600"
                onClick={onPrev}
                aria-label="Previous"
              >
                <SafeIcon icon={FiChevronLeft} />
              </button>
              <h2 className="text-lg font-bold text-gray-900 min-w-[140px] text-center">{headerLabel}</h2>
              <button
                type="button"
                className="p-2 rounded-md border border-gray-200 hover:bg-gray-50 text-gray-600"
                onClick={onNext}
                aria-label="Next"
              >
                <SafeIcon icon={FiChevronRight} />
              </button>
              <Button variant="outline" size="sm" onClick={goToday}>
                Today
              </Button>
            </div>
            <div className="inline-flex rounded-full border border-gray-200 p-0.5 bg-gray-50">
              {VIEW_OPTIONS.map((opt) => (
                <button
                  key={opt.value}
                  type="button"
                  onClick={() => {
                    setViewMode(opt.value);
                    if (opt.value === 'day') setFocusDate(selectedDate);
                  }}
                  className={`px-4 py-1.5 text-sm font-medium rounded-full transition-colors ${
                    viewMode === opt.value ? 'bg-white text-primary shadow-sm' : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>

          <div className="relative min-h-[28rem]">
            {loading && (
              <div className="absolute inset-0 z-10 flex items-center justify-center bg-white/70 rounded-lg">
                <Spinner size="lg" className="text-primary" />
              </div>
            )}
            {!loading && filteredAppointments.length === 0 && viewMode === 'month' && (
              <p className="text-sm text-gray-500 mb-2 px-1">No appointments in this period.</p>
            )}
            {viewMode === 'month' ? (
              <CalendarMonthView
                focusDate={focusDate}
                selectedDate={selectedDate}
                appointments={filteredAppointments}
                onSelectDay={handleSelectDay}
                onSelectAppointment={setDetailAppt}
                onExpandDay={(day) => {
                  setSelectedDate(day);
                  setMobileSheetOpen(true);
                }}
              />
            ) : (
              <CalendarTimeGridView
                viewMode={viewMode}
                focusDate={viewMode === 'day' ? selectedDate : focusDate}
                appointments={filteredAppointments}
                onSelectAppointment={setDetailAppt}
                onEmptySlotClick={
                  viewMode === 'day'
                    ? (iso) => openBookFlow({ date: selectedDate, slotStart: iso })
                    : undefined
                }
              />
            )}
          </div>
        </div>

        <DayDetailPanel
          className="hidden lg:flex lg:w-[320px] shrink-0 rounded-lg border border-gray-200 shadow-sm overflow-hidden lg:max-h-[calc(100vh-14rem)]"
          selectedDate={selectedDate}
          appointments={filteredAppointments}
          loading={loading}
          onNewAppointment={() => openBookFlow({ date: selectedDate })}
          onOpenAppointment={setDetailAppt}
          onComplete={handleComplete}
          onCancel={handleCancel}
          onReschedule={openBookForAppointment}
          onScheduleEmpty={() => openBookFlow({ date: selectedDate })}
        />
      </div>

      {mobileSheetOpen && (
        <div className="lg:hidden fixed inset-0 z-40 flex flex-col justify-end">
          <button
            type="button"
            className="absolute inset-0 bg-black/40"
            aria-label="Close"
            onClick={() => setMobileSheetOpen(false)}
          />
          <div className="relative bg-white rounded-t-xl max-h-[75vh] flex flex-col shadow-xl">
            <DayDetailPanel
              selectedDate={selectedDate}
              appointments={filteredAppointments}
              loading={loading}
              onClose={() => setMobileSheetOpen(false)}
              onNewAppointment={() => {
                openBookFlow({ date: selectedDate });
                setMobileSheetOpen(false);
              }}
              onOpenAppointment={(appt) => {
                setDetailAppt(appt);
                setMobileSheetOpen(false);
              }}
              onComplete={handleComplete}
              onCancel={handleCancel}
              onReschedule={openBookForAppointment}
              onScheduleEmpty={() => openBookFlow({ date: selectedDate })}
            />
          </div>
        </div>
      )}

      <AppointmentDetailModal
        appointment={detailAppt}
        isOpen={Boolean(detailAppt)}
        onClose={() => setDetailAppt(null)}
        onUpdated={loadAppointments}
        onReschedule={(appt) => {
          setDetailAppt(null);
          openBookForAppointment(appt);
        }}
      />

      <SelectLeadModal
        isOpen={selectLeadOpen}
        onClose={() => {
          setSelectLeadOpen(false);
          setPendingBook(null);
        }}
        agencyId={user?.agency_id}
        onSelect={handleLeadSelected}
      />

      {bookLead && (
        <BookAppointmentModal
          isOpen={bookOpen}
          onClose={() => {
            setBookOpen(false);
            setBookLead(null);
            setBookExisting(null);
            setBookInitialDate(null);
            setBookInitialSlot(null);
          }}
          lead={bookLead}
          existingAppointment={bookExisting}
          initialDate={bookInitialDate}
          initialSlotStart={bookInitialSlot}
          onSuccess={loadAppointments}
        />
      )}
    </div>
  );
}
